import React, { FC, useCallback, useMemo } from 'react';
import { useTheme, EngineTheme, useMergeClasses } from '../../../../theme';
import { useTranslation } from 'react-i18next';

import { useConfig } from '../../../../context';

import AgreementCheckboxWrapper, {
	AgreementCheckboxChildrenProps,
	AgreementCheckboxType
} from '../../../../AgreementCheckboxWrapper';
import { BaseButton } from '../../../../BaseComponents';
import CdnImage from '../../../../CdnImage';
import { SafeHtml } from '@shared/ui';

export interface Props {
	classes?: EngineTheme['LoginPage']['FirstLoginAgreement'];
	checkboxes?: AgreementCheckboxType[];
	loading?: boolean;
	onConfirm: () => void;
}

const FirstLoginAgreement: FC<Props> = ({ classes, checkboxes: initialCheckboxes, loading, onConfirm }) => {
	const { FirstLoginAgreement: defaultClasses } = useTheme('LoginPage');
	const css = useMergeClasses(defaultClasses, classes);

	const { t } = useTranslation('LoginPage');

	const {
		global: { companyInfo }
	} = useConfig();

	const checkboxes = useMemo<AgreementCheckboxType[]>(() => {
		if (initialCheckboxes?.length > 0) {
			return initialCheckboxes;
		}

		const defaultCheckboxes: AgreementCheckboxType[] = [
			{
				fieldName: 'agreement',
				label: (
					<SafeHtml
						tag="span"
						className={css.checkbox__label}
						html={t('FirstLoginAgreement_Checkbox_Label', {
							privacyPolicy: companyInfo.privacyPolicy
						})}
					/>
				),
				required: true
			}
		];

		return defaultCheckboxes;
	}, [t, initialCheckboxes, companyInfo]);

	const renderButton = useCallback(
		({ isValid }: AgreementCheckboxChildrenProps) => {
			const handleConfirm = () => {
				if (!isValid) {
					return;
				}

				onConfirm();
			};

			return (
				<BaseButton
					className={css.button}
					disabled={!isValid}
					isLoading={loading}
					onClick={handleConfirm}
					variant="action"
				>
					{t('FirstLoginAgreement_Button')}
				</BaseButton>
			);
		},
		[t, onConfirm, loading]
	);

	return (
		<div className={css.root}>
			<AgreementCheckboxWrapper
				classes={{
					footer_wrapper: css.footer_wrapper,
					checkboxes_wrapper: css.checkboxes_wrapper
				}}
				checkboxes={checkboxes}
				showTooltip={false}
				renderButton={renderButton}
			>
				{() => (
					<div className={css.content}>
						<div className={css.title}>
							<CdnImage
								className={css.emojy}
								path="https://cdn.websky.aero/content/frontend/images/emojy/hello.svg"
							/>

							{t('FirstLoginAgreement_Title')}
						</div>

						<div className={css.message}>{t('FirstLoginAgreement_Description')}</div>
					</div>
				)}
			</AgreementCheckboxWrapper>
		</div>
	);
};

export default FirstLoginAgreement;
